<template>
  <div class="v3-form limit-location-max-cap-form">
    <div class="row">
      <div class="mt-3">
        <Label html-for="llmc-cap-limit" label-id="llmc-cap-limit-lbl">
          Cap limit
        </Label>
        <InputField
          id="llmc-cap-limit"
          label-id="llmc-cap-limit-lbl"
          placeholder="Limit"
          v-model="state.form.limit"
          :invalid-feedback="errors.limit || state.serverErrors.limit"
          class="d-block"
          @keypress="validateLimitInput"
        ></InputField>
      </div>
      <div class="mt-2">
        <InfoBox>A "-" dash indicates unlimited.</InfoBox>
      </div>
      <div class="my-3">
        <div v-if="!editableLimitData">
          <LazyLoadSelect
            :is-multiple="true"
            class="flex-1 me-2"
            :selected="state.form.locations"
            type="locations"
            :added-options="addedLocations"
            :force-clear-cache="true"
            input-id="llmc-select-locations"
            accessible-label="Select locations"
            placeholder="Select locations"
            @update:model-value="(value) => updateData(value)"
          />
        </div>
        <div v-else>
          <Label
            html-for="llmc-location-name"
            label-id="llmc-location-name-lbl"
          >
            Location
          </Label>
          <InputField
            id="llmc-location-name"
            label-id="llmc-location-name-lbl"
            v-model="state.form.edited_location_name"
            :disabled="true"
            class="d-block"
          ></InputField>
        </div>
      </div>
      <!--
        Live region + focus: CoreUI CAlert does not expose role/aria-live on the DOM root, and polite
        regions inside aria-modal often fail to announce. assertive + move focus here after save (APG).
      -->
      <div
        v-if="state.feedbackAlert.message"
        ref="feedbackAlertRegionRef"
        class="mt-2"
        tabindex="-1"
        :role="state.feedbackAlert.color === 'danger' ? 'alert' : 'status'"
        aria-live="assertive"
        aria-atomic="true"
        data-testid="llmc-form-feedback-alert"
      >
        <CAlert
          dismissible
          class="mb-0"
          :color="state.feedbackAlert.color"
          :show="true"
          @close="dismissFeedbackAlert"
        >
          <div class="pe-4">
            <div v-if="state.feedbackAlert.title" class="fw-semibold mb-1">
              {{ state.feedbackAlert.title }}
            </div>
            <div class="mb-0">{{ state.feedbackAlert.message }}</div>
          </div>
        </CAlert>
      </div>
      <div
        class="d-flex align-items-center justify-content-center mt-4 text-center"
      >
        <LoadingButton
          v-if="!isHeaderCreateButton"
          class="me-3 px-4"
          @click="onCancel()"
          rounded
          >Cancel</LoadingButton
        >
        <LoadingButton
          :is-loading="state.isProcessing"
          @click="submit(editableLimitData)"
          class="px-4 me-3"
          solid
          rounded
          >{{ editableLimitData ? "Update" : "Create" }}</LoadingButton
        >
        <router-link
          v-if="isHeaderCreateButton"
          class="text-graident-blue d-inline-flex align-items-center text-decoration-none"
          to="/limits/limit-location-max-cap"
          @click="onCancel"
        >
          <img
            width="18"
            class="m-0 me-2"
            alt=""
            src="@/assets/images/icons/gradient-link.png"
          />
          <span class="text-graident-blue">Go to full menu</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  onMounted,
  onUnmounted,
  inject,
  ref,
  watch,
  nextTick
} from "vue"

const SUCCESS_MODAL_CLOSE_DELAY_MS = 2400
import { useStore } from "vuex"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import useVuelidate from "@vuelidate/core"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"

export default {
  name: "CreateLimitLocationMaxCapForm",
  components: {
    Label,
    InputField,
    LazyLoadSelect,
    LoadingButton,
    InfoBox
  },
  props: {
    editableLimitData: {
      type: Object
    },
    isHeaderCreateButton: {
      type: Boolean,
      default: false
    },
    isDashboardView: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "closeModal"],
  setup(props, { emit }) {
    const store = useStore()
    const modal = inject("modal")

    const state = reactive({
      isProcessing: false,
      form: {
        limit: "-",
        locations: [],
        edited_location_name: ""
      },
      serverErrors: {},
      feedbackAlert: {
        title: "",
        message: "",
        color: "success"
      }
    })

    const addedLocations = ref([])
    const feedbackAlertRegionRef = ref(null)
    let successCloseTimerId = null

    const clearSuccessCloseTimer = () => {
      if (successCloseTimerId != null) {
        clearTimeout(successCloseTimerId)
        successCloseTimerId = null
      }
    }

    const scheduleModalCloseAfterSuccess = () => {
      clearSuccessCloseTimer()
      successCloseTimerId = window.setTimeout(() => {
        successCloseTimerId = null
        emit("closeModal")
        emit("cancel")
      }, SUCCESS_MODAL_CLOSE_DELAY_MS)
    }

    /** When feedback appears (especially inside `aria-modal`), move focus so SRs announce it reliably. */
    watch(
      () => state.feedbackAlert.message,
      async (msg) => {
        if (!msg) return
        await nextTick()
        requestAnimationFrame(() => {
          const el = feedbackAlertRegionRef.value
          if (el && typeof el.focus === "function") {
            try {
              el.focus({ preventScroll: true })
            } catch {
              el.focus()
            }
          }
        })
      }
    )

    const validationMessages = {
      required: "This field is required",
      limit:
        'The limit must a number or a "-" dash (where a "-" means unlimited).'
    }
    const validations = {
      form: {
        limit: {
          required: helpers.withMessage(validationMessages.limit, required)
        },
        locations: {
          required: helpers.withMessage(
            validationMessages.required,
            requiredIf(() => {
              return !props.editableLimitData
            })
          )
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    onMounted(() => {
      if (props.editableLimitData) {
        setFormByLimitRecord(props.editableLimitData)
      }
      store
        .dispatch("rooms/getLocationsWithRoomCapacity")
        .then((resp) => {
          const result = resp?.data?.rooms || []
          addedLocations.value = result.filter(Boolean).map((location) => {
            if (typeof location === "object" && location !== null) {
              return {
                label: location.name || `Location ID: ${location.id}`,
                value: {
                  id: location.id,
                  type: "App\\Models\\Room"
                }
              }
            } else {
              return {
                label: `Location ID: ${location}`,
                value: {
                  id: location,
                  type: "App\\Models\\Room"
                }
              }
            }
          })
        })
        .catch(() => {
          addedLocations.value = []
        })
    })

    const updateData = (value) => {
      state.form.locations = value
      state.serverErrors = {}
      clearFeedbackAlert()
    }

    const setFormByLimitRecord = (limit) => {
      if (limit) {
        state.form = {
          limit: limit.limit != null ? limit.limit : "-",
          locations: limit.room.id ? [limit.room.id] : [],
          edited_location_name: limit.room.name ? limit.room.name : null
        }
      }
    }

    const submit = (isUpdate) => {
      if (isFormValid.value) {
        // Extract just the IDs from the selected location objects
        const selectedIds = Array.isArray(state.form.locations)
          ? state.form.locations.map(
              (location) => location.value?.id || location.id || location
            )
          : [
              state.form.locations.value?.id ||
                state.form.locations.id ||
                state.form.locations
            ]

        const data = {
          limit: state.form.limit === "-" ? null : parseInt(state.form.limit),
          room_ids: selectedIds && selectedIds.length ? selectedIds : null
        }
        if (isUpdate && props.editableLimitData.id) {
          updateLimitLocation(data)
        } else {
          createLimitLocation(data)
        }
      } else {
        v$.value.$touch()
      }
    }

    const createLimitLocation = (data) => {
      state.isProcessing = true
      store
        .dispatch("rooms/createRoomCapacityLimit", data)
        .then((response) => {
          const dataResponse = response.data.data
          setFeedbackAlert("Success", "Successfully saved!", "success")
          state.isProcessing = false
          state.serverErrors = {}
          modal.setAction("createLimitLocationMaxCap")
          nextTick(() => {
            scheduleModalCloseAfterSuccess()
          })
          if (dataResponse) {
            if (props.isDashboardView) {
              store.commit("rooms/UPDATE_ROOM_CAPACITY_LIMIT", dataResponse)
            } else {
              store.dispatch("rooms/getRoomCapacityLimit").then((response) => {
                const dataGetTable = response.data.data
                if (dataGetTable) {
                  store.commit("rooms/PUSH_ROOM_CAPACITY_LIMITS", dataGetTable)
                }
              })
            }
          }
        })
        .catch((err) => {
          clearSuccessCloseTimer()
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setFeedbackAlert("Error", res.message, "danger")
          state.isProcessing = false
        })
    }

    const updateLimitLocation = (data) => {
      state.isProcessing = true
      const dataLimit = {
        limit: data.limit,
        id: props.editableLimitData.id
      }
      store
        .dispatch("rooms/updateRoomCapacityLimit", dataLimit)
        .then((response) => {
          setFeedbackAlert("Success", "Successfully updated!", "success")
          state.isProcessing = false
          state.serverErrors = {}
          const newData = response.data.data
          if (newData) {
            store.commit("rooms/UPDATE_ROOM_CAPACITY_LIMIT", newData)
          }
          nextTick(() => {
            scheduleModalCloseAfterSuccess()
          })
        })
        .catch((err) => {
          clearSuccessCloseTimer()
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setFeedbackAlert("Error", res.message, "danger")
          state.isProcessing = false
        })
    }

    const validateLimitInput = (event) => {
      if (event.keyCode >= 48 && event.keyCode <= 59) {
        if (state.form.limit === "-") {
          state.form.limit = ""
        }
        return true
      } else if (event.keyCode == 45) {
        state.form.limit = ""
        return true
      } else {
        return event.preventDefault()
      }
    }

    const setFeedbackAlert = (title, message, color) => {
      if (title || message) {
        state.feedbackAlert = {
          title: title || "",
          message: message || "",
          color: color || "success"
        }
      } else {
        state.feedbackAlert = { title: "", message: "", color: "success" }
      }
    }

    const clearFeedbackAlert = () => {
      setFeedbackAlert()
    }

    const dismissFeedbackAlert = () => {
      clearSuccessCloseTimer()
      clearFeedbackAlert()
    }

    const onCancel = () => {
      clearSuccessCloseTimer()
      resetFormData()
      emit("cancel")
      emit("closeModal")
    }

    onUnmounted(() => {
      clearSuccessCloseTimer()
    })

    const resetFormData = () => {
      state.form.limit = "-"
      state.form.locations = null
    }

    return {
      state,
      v$,
      updateData,
      validateLimitInput,
      submit,
      isFormValid,
      errors,
      setFeedbackAlert,
      dismissFeedbackAlert,
      onCancel,
      resetFormData,
      addedLocations,
      feedbackAlertRegionRef
    }
  }
}
</script>
